import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { publicAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { 
  BookOpen, Video, Calendar, Clock, Users, 
  MonitorPlay, ExternalLink, ChevronRight, Play
} from 'lucide-react';

const PROGRAMS = [
  'UTBK-SNBT',
  'CPNS',
  'TNI',
  'POLRI',
  'SEKOLAH KEDINASAN',
  'BUMN',
  'TKA (Tes Kemampuan Akademik)',
  'SMA Unggul/Favorit'
];

export default function VideoPage() {
  const { user } = useAuth();
  const [zoomClasses, setZoomClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadZoomClasses();
  }, [filter]);

  const loadZoomClasses = async () => {
    try {
      setLoading(true);
      const res = await publicAPI.getZoomClasses(filter === 'all' ? null : filter);
      setZoomClasses(res.data);
    } catch (err) {
      console.error('Failed to load zoom classes:', err);
    } finally {
      setLoading(false);
    }
  };

  // Separate upcoming and past classes
  const now = new Date();
  const upcomingClasses = zoomClasses.filter(c => {
    const classDate = new Date(`${c.schedule_date}T${c.schedule_time}`);
    return classDate >= now;
  }).sort((a, b) => {
    const dateA = new Date(`${a.schedule_date}T${a.schedule_time}`);
    const dateB = new Date(`${b.schedule_date}T${b.schedule_time}`);
    return dateA - dateB;
  });

  const pastClasses = zoomClasses.filter(c => {
    const classDate = new Date(`${c.schedule_date}T${c.schedule_time}`);
    return classDate < now;
  }).sort((a, b) => {
    const dateA = new Date(`${a.schedule_date}T${a.schedule_time}`);
    const dateB = new Date(`${b.schedule_date}T${b.schedule_time}`);
    return dateB - dateA;
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="video-page">
      {/* Header with Mobile Navigation */}
      <Header />

      {/* Hero */}
      <section className="bg-[#4472C4] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <Badge className="bg-white/20 text-white hover:bg-white/30 border-0 mb-4">Kelas Online Live</Badge>
          <h1 className="text-4xl font-bold mb-4">Kelas Zoom Live</h1>
          <p className="text-lg opacity-90 max-w-2xl mx-auto">
            Ikuti kelas online bersama tentor berpengalaman secara live melalui Zoom
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-6 px-4 bg-white border-b">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-slate-600">Filter Program:</span>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[200px]" data-testid="program-filter">
                <SelectValue placeholder="Semua Program" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Program</SelectItem>
                {PROGRAMS.map(prog => (
                  <SelectItem key={prog} value={prog}>{prog}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-slate-500">
            {zoomClasses.length} kelas ditemukan
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto space-y-12">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-[#4472C4] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-4 text-slate-500">Memuat kelas...</p>
            </div>
          ) : zoomClasses.length === 0 ? (
            <div className="text-center py-12">
              <MonitorPlay className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Belum Ada Kelas</h3>
              <p className="text-slate-500 mb-6">Saat ini belum ada jadwal kelas Zoom yang tersedia</p>
              <Link to="/packages">
                <Button className="bg-[#4472C4] hover:bg-[#3861a8]">
                  Lihat Paket Try Out
                </Button>
              </Link>
            </div>
          ) : (
            <>
              {/* Upcoming Classes */}
              {upcomingClasses.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Play className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">Kelas Mendatang</h2>
                      <p className="text-sm text-slate-500">{upcomingClasses.length} kelas yang akan datang</p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {upcomingClasses.map((c) => (
                      <Card key={c.id} className="hover:shadow-lg transition-all" data-testid={`zoom-class-${c.id}`}>
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Mendatang</Badge>
                            <Badge variant="secondary">{c.program}</Badge>
                          </div>
                          <CardTitle className="text-lg mt-2">{c.title}</CardTitle>
                          <CardDescription className="line-clamp-2">{c.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3 text-sm text-slate-600 mb-4">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-slate-400" />
                              <span>Instruktur: <strong>{c.instructor}</strong></span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              <span>{formatDate(c.schedule_date)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-slate-400" />
                              <span>{c.schedule_time} WIB ({c.duration_minutes} menit)</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MonitorPlay className="w-4 h-4 text-slate-400" />
                              <span>Peserta: {c.registered_count}/{c.max_participants}</span>
                            </div>
                          </div>
                          
                          <a href={c.zoom_link} target="_blank" rel="noopener noreferrer" className="block">
                            <Button className="w-full bg-[#4472C4] hover:bg-[#3861a8]">
                              <Video className="w-4 h-4 mr-2" />
                              Join Zoom
                              <ExternalLink className="w-4 h-4 ml-2" />
                            </Button>
                          </a>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Past Classes (Archives) */}
              {pastClasses.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900">Arsip Kelas</h2>
                      <p className="text-sm text-slate-500">{pastClasses.length} kelas yang sudah berlalu</p>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {pastClasses.slice(0, 6).map((c) => (
                      <Card key={c.id} className="opacity-75 hover:opacity-100 transition-all" data-testid={`past-class-${c.id}`}>
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <Badge variant="secondary">Selesai</Badge>
                            <Badge variant="outline">{c.program}</Badge>
                          </div>
                          <CardTitle className="text-lg mt-2">{c.title}</CardTitle>
                          <CardDescription className="line-clamp-2">{c.description}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm text-slate-500 mb-4">
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4" />
                              <span>Instruktur: {c.instructor}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(c.schedule_date)}</span>
                            </div>
                          </div>
                          
                          <Button variant="outline" className="w-full" disabled>
                            Kelas Sudah Berakhir
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-[#4472C4]">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ingin Ikut Kelas Live?</h2>
          <p className="text-lg opacity-90 mb-8">
            Daftar sekarang dan dapatkan akses ke semua kelas online kami
          </p>
          <Link to="/register">
            <Button className="bg-[#FF6B35] hover:bg-[#e55a2a] text-white rounded-lg px-8 py-6 text-lg" data-testid="cta-register">
              Daftar Gratis Sekarang
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
